import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import Layout from '@/components/Layout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ClipboardList, ShieldCheck, AlertTriangle, Building, Layers, FileText } from 'lucide-react';

const STATUS_COLORS = {
  not_started: 'bg-blue-500', in_progress: 'bg-amber-500',
  complete: 'bg-emerald-500', blocked: 'bg-red-500',
};
const STATUS_LABELS = {
  not_started: 'Not Started', in_progress: 'In Progress',
  complete: 'Complete', blocked: 'Blocked',
};

export default function UnitDetail() {
  const { unitId } = useParams();
  const navigate = useNavigate();
  const [unit, setUnit] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/units/${unitId}`).then(r => setUnit(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [unitId]);

  if (loading) return <Layout><div className="flex items-center justify-center py-16 text-slate-400">Loading...</div></Layout>;
  if (!unit) return <Layout><div className="text-center py-16 text-slate-400">Unit not found</div></Layout>;

  const stagesByType = (type) => (unit.stages_data || []).filter(s => s.stage_type === type).sort((a, b) => a.stage_number - b.stage_number);
  const progressStages = stagesByType('progress');
  const qaStages = stagesByType('qa');

  return (
    <Layout>
      <div className="space-y-6" data-testid="unit-detail-page">
        <div className="flex items-center gap-3">
          {unit.type === 'communal' ? <Layers className="h-6 w-6 text-emerald-600" /> : <Building className="h-6 w-6 text-blue-600" />}
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit' }}>{unit.name}</h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="outline" className="text-[10px] capitalize">{unit.type}</Badge>
              {unit.open_issues > 0 && (
                <Badge className="bg-red-100 text-red-700 text-[10px]">
                  <AlertTriangle className="h-3 w-3 mr-1" />{unit.open_issues} open issue{unit.open_issues > 1 ? 's' : ''}
                </Badge>
              )}
            </div>
          </div>
        </div>

        {/* Two large buttons */}
        <div className="grid grid-cols-1 gap-4">
          <button
            onClick={() => navigate(`/units/${unitId}/progress`)}
            className="flex flex-col items-center justify-center w-full p-8 rounded-xl bg-white border-2 border-slate-200 hover:border-blue-500 hover:bg-blue-50 transition-all group cursor-pointer"
            data-testid="progress-button"
          >
            <ClipboardList className="h-10 w-10 text-blue-600 mb-3 group-hover:scale-110 transition-transform" />
            <span className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Progress</span>
            <span className="text-sm text-slate-500 mt-1">Track construction stages</span>
            <div className="flex items-center gap-3 mt-4">
              {progressStages.map(s => (
                <div key={s.stage_number} className="flex items-center gap-1.5">
                  <div className={`w-3 h-3 rounded-full ${STATUS_COLORS[s.status] || 'bg-slate-300'}`} />
                  <span className="text-xs text-slate-600">Stage {s.stage_number}</span>
                </div>
              ))}
            </div>
          </button>

          <button
            onClick={() => navigate(`/units/${unitId}/qa`)}
            className="flex flex-col items-center justify-center w-full p-8 rounded-xl bg-white border-2 border-slate-200 hover:border-emerald-500 hover:bg-emerald-50 transition-all group cursor-pointer"
            data-testid="qa-button"
          >
            <ShieldCheck className="h-10 w-10 text-emerald-600 mb-3 group-hover:scale-110 transition-transform" />
            <span className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Quality Assurance</span>
            <span className="text-sm text-slate-500 mt-1">QA inspections & sign-off</span>
            <div className="flex items-center gap-3 mt-4">
              {qaStages.map(s => (
                <div key={s.stage_number} className="flex items-center gap-1.5">
                  <div className={`w-3 h-3 rounded-full ${STATUS_COLORS[s.status] || 'bg-slate-300'}`} />
                  <span className="text-xs text-slate-600">Stage {s.stage_number}</span>
                </div>
              ))}
            </div>
          </button>
        </div>

        {/* Stage summary */}
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <h3 className="text-sm font-semibold text-slate-800 mb-3" style={{ fontFamily: 'Outfit' }}>Stage Summary</h3>
          <div className="grid grid-cols-2 gap-3">
            {[...progressStages, ...qaStages].map(s => (
              <div key={`${s.stage_type}_${s.stage_number}`} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                <span className="text-xs text-slate-600">{s.stage_name}</span>
                <Badge className={`text-[10px] text-white ${STATUS_COLORS[s.status]}`}>
                  {STATUS_LABELS[s.status]}
                </Badge>
              </div>
            ))}
          </div>
        </div>

        {/* Audit link */}
        <Button variant="outline" className="w-full text-sm" onClick={() => navigate(`/audit/${unitId}`)} data-testid="unit-audit-button">
          <FileText className="h-4 w-4 mr-2" />View Audit Trail for {unit.name}
        </Button>
      </div>
    </Layout>
  );
}
